import { useEffect, useState } from 'react';
import { FolderPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type AppFolder, loadAppFolders, placeInAppFolder, saveAppFolder } from '@/lib/app-collaboration';

interface AppFolderPickerProps {
  recordId: string;
  teams: Array<{ id: string; name: string }>;
}

/** Put an object in a folder (across apps), or create the folder on the way. */
export const AppFolderPicker = ({ recordId, teams }: AppFolderPickerProps) => {
  const [folders, setFolders] = useState<AppFolder[]>([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [teamId, setTeamId] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void loadAppFolders().then(setFolders).catch((cause) => {
      console.error('app_folders_load_failed', cause);
      setMessage('Les dossiers n’ont pas pu être chargés.');
    });
  }, []);

  const add = async (folderId: string) => {
    setMessage(null);
    try {
      await placeInAppFolder(folderId, recordId);
      setMessage(`Rangée dans « ${folders.find((folder) => folder.id === folderId)?.name ?? name} ».`);
      setFolders(await loadAppFolders());
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'Le dossier n’a pas pu être modifié.');
    }
  };

  const create = async () => {
    if (!name.trim()) return;
    try {
      const id = await saveAppFolder({ name: name.trim(), visibility: teamId ? 'team' : 'private', teamId: teamId || null });
      setCreating(false);
      await add(id);
      setName('');
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'Le dossier n’a pas pu être créé.');
    }
  };

  return (
    <div>
      <p className="text-xs text-domino-inkSoft">Dossier</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {folders.length > 0 && (
          <select defaultValue="" aria-label="Ranger dans un dossier" onChange={(event) => { if (event.target.value) void add(event.target.value); event.target.value = ''; }}
            className="rounded-lg border border-domino-cardBorder px-2 py-1 text-sm text-domino-ink">
            <option value="">Ranger dans un dossier…</option>
            {folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
          </select>
        )}
        {creating ? (
          <form className="flex flex-wrap items-center gap-2" onSubmit={(event) => { event.preventDefault(); void create(); }}>
            <input autoFocus value={name} onChange={(event) => setName(event.target.value)} maxLength={80} placeholder="Nom du dossier"
              aria-label="Nom du nouveau dossier" className="w-44 rounded-lg border border-domino-cardBorder px-2 py-1 text-sm" />
            <select value={teamId} onChange={(event) => setTeamId(event.target.value)} aria-label="Partagé avec" className="rounded-lg border border-domino-cardBorder px-2 py-1 text-sm">
              <option value="">Juste moi</option>
              {teams.map((team) => <option key={team.id} value={team.id}>Équipe {team.name}</option>)}
            </select>
            <button type="submit" className="rounded-lg px-2 py-1 text-sm text-domino-aubergine hover:bg-domino-muted">Créer et ranger</button>
          </form>
        ) : (
          <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-domino-inkSoft hover:bg-domino-muted">
            <FolderPlus className="h-4 w-4" aria-hidden="true" />Nouveau dossier
          </button>
        )}
        <Link to="/folders" className="text-xs text-domino-aubergine underline">Voir les dossiers</Link>
      </div>
      {message && <p className="mt-1 text-xs text-domino-inkSoft" role="status">{message}</p>}
    </div>
  );
};
